import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import PortalLayout from '@/components/portal/PortalLayout';
// AdminRoute removed - AuthMiddleware handles all permission checking
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

interface Plan {
  id: string;
  name: string;
  description: string | null;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PlanManagementPage: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stripe_price_id: '',
    price: '',
    features: '',
    max_chatbots: '',
    max_conversations_per_month: '',
    is_active: true,
  });

  const { data: plans, isLoading, error, isError } = useQuery<Plan[], Error>({
    queryKey: ['adminPlans'],
    queryFn: async () => {
      const { getPlans } = await import('@/api/admin');
      return await getPlans();
    },
  });

  const createPlanMutation = useMutation({
    mutationFn: async (planData: any) => {
      const { createPlan } = await import('@/api/admin');
      return await createPlan({
        name: planData.name,
        description: planData.description || null,
        stripe_price_id: planData.stripe_price_id,
        price: parseFloat(planData.price),
        features: planData.features ? planData.features.split(',').map((f: string) => f.trim()) : [],
        max_chatbots: parseInt(planData.max_chatbots) || 1,
        max_conversations_per_month: planData.max_conversations_per_month ? parseInt(planData.max_conversations_per_month) : null,
        is_active: planData.is_active,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Plan created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['adminPlans'] });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create plan.",
        variant: "destructive",
      });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, planData }: { id: string; planData: any }) => {
      const { updatePlan } = await import('@/api/admin');
      return await updatePlan(id, {
        name: planData.name,
        description: planData.description || null,
        stripe_price_id: planData.stripe_price_id,
        price: parseFloat(planData.price),
        features: planData.features ? planData.features.split(',').map((f: string) => f.trim()) : [],
        max_chatbots: parseInt(planData.max_chatbots) || 1,
        max_conversations_per_month: planData.max_conversations_per_month ? parseInt(planData.max_conversations_per_month) : null,
        is_active: planData.is_active,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Plan updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['adminPlans'] });
      setEditingPlan(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update plan.",
        variant: "destructive",
      });
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (planId: string) => {
      const { deletePlan } = await import('@/api/admin');
      return await deletePlan(planId);
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Plan deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['adminPlans'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete plan.",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      stripe_price_id: '',
      price: '',
      features: '',
      max_chatbots: '',
      max_conversations_per_month: '',
      is_active: true,
    });
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      stripe_price_id: plan.stripe_price_id,
      price: plan.price.toString(),
      features: plan.features.join(', '),
      max_chatbots: plan.max_chatbots.toString(),
      max_conversations_per_month: plan.max_conversations_per_month?.toString() || '',
      is_active: plan.is_active,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPlan) {
      updatePlanMutation.mutate({ id: editingPlan.id, planData: formData });
    } else {
      createPlanMutation.mutate(formData);
    }
  };

  const handleDelete = (planId: string) => {
    if (confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
      deletePlanMutation.mutate(planId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load plans: {error?.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Plan Management</h1>
            <p className="text-gray-600 mt-2">
              Manage subscription plans and pricing for your application.
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setEditingPlan(null); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Plan
              </Button>
            </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
                  <DialogDescription>
                    {editingPlan ? 'Update the plan details below.' : 'Fill in the details for the new subscription plan.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Plan Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="stripe_price_id">Stripe Price ID</Label>
                      <Input
                        id="stripe_price_id"
                        value={formData.stripe_price_id}
                        onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="price">Price ($)</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_chatbots">Max Chatbots</Label>
                      <Input
                        id="max_chatbots"
                        type="number"
                        value={formData.max_chatbots}
                        onChange={(e) => setFormData({ ...formData, max_chatbots: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_conversations_per_month">Max Conversations/Month</Label>
                      <Input
                        id="max_conversations_per_month"
                        type="number"
                        value={formData.max_conversations_per_month}
                        onChange={(e) => setFormData({ ...formData, max_conversations_per_month: e.target.value })}
                        placeholder="Leave empty for unlimited"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="features">Features (comma-separated)</Label>
                    <Textarea
                      id="features"
                      value={formData.features}
                      onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                      placeholder="Feature 1, Feature 2, Feature 3"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active">Plan is active</Label>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => {
                      setIsCreateDialogOpen(false);
                      setEditingPlan(null);
                      resetForm();
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createPlanMutation.isPending || updatePlanMutation.isPending}>
                      {(createPlanMutation.isPending || updatePlanMutation.isPending) && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {editingPlan ? 'Update Plan' : 'Create Plan'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

        {!isLoading && !isError && plans && (
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Max Chatbots</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stripe Price ID</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No plans found.
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.name}</TableCell>
                      <TableCell>{plan.description || 'N/A'}</TableCell>
                      <TableCell>${plan.price}</TableCell>
                      <TableCell>{plan.max_chatbots === -1 ? 'Unlimited' : plan.max_chatbots}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          plan.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{plan.stripe_price_id}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handleEdit(plan);
                              setIsCreateDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(plan.id)}
                            disabled={deletePlanMutation.isPending}
                          >
                            {deletePlanMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
          </Table>
        )}

      </div>
    </PortalLayout>
  );
};

export default PlanManagementPage;
